import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Target,
  LayoutDashboard,
  GitBranch,
  FileText,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  ExternalLink,
  Clock,
  Tag,
  Layers,
  Search,
  X,
  AlertCircle,
  Loader2,
  Building2,
  Mic,
  FileUp,
  Lock,
  Crown
} from 'lucide-react';
import { PageLayout } from '@/components';
import type {
  ArchitectureVision,
  SystemArchitecture,
  SolutionDesignDocument,
  ArchitectureDecisionRecord,
  ArchitectureDocType
} from '@/lib/types';
import { addApi, type ArchitectureDocMetadata } from '@/services/addApi';
import { useAuthContext } from '@/contexts/AuthContext';
import { captureError } from '@/lib/sentry';

// Line of Business definitions
interface LineOfBusiness {
  id: string;
  name: string;
  description: string;
  color: 'blue' | 'emerald' | 'purple' | 'amber';
}

const linesOfBusiness: LineOfBusiness[] = [
  { id: 'lob1', name: 'Retail Banking', description: 'Consumer banking services', color: 'blue' },
  { id: 'lob2', name: 'Corporate Banking', description: 'Enterprise financial services', color: 'emerald' },
  { id: 'lob3', name: 'Wealth Management', description: 'Investment and advisory', color: 'purple' },
  { id: 'lob4', name: 'Insurance', description: 'Insurance products and claims', color: 'amber' },
];

// Sample data organized by LOB
const sampleVisions: ArchitectureVision[] = [
  {
    id: 'vision-lob1',
    title: 'Retail Banking Digital Vision 2024',
    description: 'Strategic direction for consumer banking digital transformation, focusing on mobile-first experiences and real-time payments.',
    principles: ['Mobile-first design', 'Real-time processing', 'Open Banking APIs', 'PSD2 compliance'],
    lastUpdated: '2024-01-15',
    status: 'approved',
    systemArchitectures: ['sys-lob1-1', 'sys-lob1-2'],
  },
  {
    id: 'vision-lob2',
    title: 'Corporate Banking Platform Vision',
    description: 'Enterprise-grade banking infrastructure for corporate clients with focus on scalability and treasury management.',
    principles: ['High availability', 'Multi-currency support', 'SWIFT integration', 'Audit compliance'],
    lastUpdated: '2024-01-10',
    status: 'approved',
    systemArchitectures: ['sys-lob2-1'],
  },
  {
    id: 'vision-lob3',
    title: 'Wealth Management Digital Strategy',
    description: 'Next-generation wealth management platform enabling personalized investment advice and portfolio management.',
    principles: ['AI-driven insights', 'Real-time portfolio updates', 'Regulatory reporting', 'Client portal'],
    lastUpdated: '2024-01-08',
    status: 'approved',
    systemArchitectures: ['sys-lob3-1'],
  },
  {
    id: 'vision-lob4',
    title: 'Insurance Modernization Vision',
    description: 'Cloud-native insurance platform supporting policy management, claims processing, and risk assessment.',
    principles: ['Cloud-native', 'Event-driven claims', 'ML risk models', 'Self-service portal'],
    lastUpdated: '2024-01-05',
    status: 'approved',
    systemArchitectures: ['sys-lob4-1'],
  },
];

const sampleSystems: SystemArchitecture[] = [
  // LOB1 - Retail Banking
  {
    id: 'sys-lob1-1',
    title: 'Mobile Banking Platform',
    description: 'Customer-facing mobile application for retail banking services including account management, transfers, and payments.',
    domain: 'Digital Channels',
    techStack: ['React Native', 'Node.js', 'PostgreSQL', 'Redis'],
    lastUpdated: '2024-01-12',
    status: 'active',
    visionId: 'vision-lob1',
    solutionDesigns: ['sdd-lob1-1'],
  },
  {
    id: 'sys-lob1-2',
    title: 'Real-Time Payments Engine',
    description: 'Core payment processing system supporting instant payments, scheduled transfers, and international remittances.',
    domain: 'Payments',
    techStack: ['Java', 'Kafka', 'Oracle', 'Kubernetes'],
    lastUpdated: '2024-01-10',
    status: 'active',
    visionId: 'vision-lob1',
    solutionDesigns: ['sdd-lob1-2'],
  },
  // LOB2 - Corporate Banking
  {
    id: 'sys-lob2-1',
    title: 'Treasury Management System',
    description: 'Enterprise treasury platform for cash management, liquidity forecasting, and multi-currency operations.',
    domain: 'Treasury',
    techStack: ['Python', 'PostgreSQL', 'Apache Airflow', 'AWS'],
    lastUpdated: '2024-01-08',
    status: 'active',
    visionId: 'vision-lob2',
    solutionDesigns: ['sdd-lob2-1'],
  },
  // LOB3 - Wealth Management
  {
    id: 'sys-lob3-1',
    title: 'Portfolio Management Platform',
    description: 'Investment portfolio management system with real-time market data, performance analytics, and rebalancing tools.',
    domain: 'Investments',
    techStack: ['Python', 'React', 'TimescaleDB', 'Redis'],
    lastUpdated: '2024-01-05',
    status: 'active',
    visionId: 'vision-lob3',
    solutionDesigns: ['sdd-lob3-1'],
  },
  // LOB4 - Insurance
  {
    id: 'sys-lob4-1',
    title: 'Claims Processing System',
    description: 'End-to-end claims management from submission through adjudication, payment, and reporting.',
    domain: 'Claims',
    techStack: ['Go', 'PostgreSQL', 'Kafka', 'Elasticsearch'],
    lastUpdated: '2024-01-03',
    status: 'active',
    visionId: 'vision-lob4',
    solutionDesigns: ['sdd-lob4-1'],
  },
];

const sampleSolutions: SolutionDesignDocument[] = [
  {
    id: 'sdd-lob1-1',
    title: 'Biometric Authentication Implementation',
    description: 'Add Face ID and fingerprint authentication to the mobile banking app for secure and frictionless login.',
    initiative: 'Q1 2024 - Security Enhancement',
    targetDate: '2024-03-31',
    lastUpdated: '2024-01-12',
    status: 'approved',
    systemArchitectures: ['sys-lob1-1'],
    adrs: ['adr-lob1-1'],
  },
  {
    id: 'sdd-lob1-2',
    title: 'Instant Payment Settlement',
    description: 'Enable real-time gross settlement for domestic transfers with 24/7 availability.',
    initiative: 'Q1 2024 - Payments Modernization',
    targetDate: '2024-02-28',
    lastUpdated: '2024-01-10',
    status: 'implemented',
    systemArchitectures: ['sys-lob1-2'],
    adrs: ['adr-lob1-2'],
  },
  {
    id: 'sdd-lob2-1',
    title: 'Multi-Currency Liquidity Pool',
    description: 'Implement centralized liquidity management across multiple currencies with automated FX optimization.',
    initiative: 'Q2 2024 - Treasury Optimization',
    targetDate: '2024-06-30',
    lastUpdated: '2024-01-08',
    status: 'proposed',
    systemArchitectures: ['sys-lob2-1'],
    adrs: ['adr-lob2-1'],
  },
  {
    id: 'sdd-lob3-1',
    title: 'AI-Powered Investment Recommendations',
    description: 'Machine learning model for personalized investment recommendations based on client risk profile and goals.',
    initiative: 'Q1 2024 - AI Initiative',
    targetDate: '2024-03-31',
    lastUpdated: '2024-01-05',
    status: 'approved',
    systemArchitectures: ['sys-lob3-1'],
    adrs: ['adr-lob3-1'],
  },
  {
    id: 'sdd-lob4-1',
    title: 'Automated Claims Triage',
    description: 'ML-based claims routing and initial assessment to reduce processing time and improve accuracy.',
    initiative: 'Q1 2024 - Claims Automation',
    targetDate: '2024-04-30',
    lastUpdated: '2024-01-03',
    status: 'approved',
    systemArchitectures: ['sys-lob4-1'],
    adrs: ['adr-lob4-1'],
  },
];

const sampleADRs: ArchitectureDecisionRecord[] = [
  {
    id: 'adr-lob1-1',
    title: 'ADR-101: Use WebAuthn for Biometric Authentication',
    context: 'Need secure biometric authentication that works across iOS and Android devices.',
    decision: 'Adopt WebAuthn standard with platform authenticators for biometric verification.',
    status: 'accepted',
    lastUpdated: '2024-01-12',
    linkedTo: [{ type: 'solution', id: 'sdd-lob1-1' }],
  },
  {
    id: 'adr-lob1-2',
    title: 'ADR-102: Kafka for Payment Event Streaming',
    context: 'Real-time payments require reliable, ordered event processing with exactly-once semantics.',
    decision: 'Use Apache Kafka as the central event backbone for payment processing.',
    status: 'accepted',
    lastUpdated: '2024-01-10',
    linkedTo: [{ type: 'solution', id: 'sdd-lob1-2' }],
  },
  {
    id: 'adr-lob2-1',
    title: 'ADR-201: Use CLS for FX Settlement',
    context: 'Multi-currency operations require settlement risk mitigation.',
    decision: 'Integrate with CLS (Continuous Linked Settlement) for FX transactions.',
    status: 'proposed',
    lastUpdated: '2024-01-08',
    linkedTo: [{ type: 'solution', id: 'sdd-lob2-1' }],
  },
  {
    id: 'adr-lob3-1',
    title: 'ADR-301: XGBoost for Investment Recommendations',
    context: 'Need explainable ML model for regulatory compliance in investment advice.',
    decision: 'Use XGBoost with SHAP values for transparent, explainable recommendations.',
    status: 'accepted',
    lastUpdated: '2024-01-05',
    linkedTo: [{ type: 'solution', id: 'sdd-lob3-1' }],
  },
  {
    id: 'adr-lob4-1',
    title: 'ADR-401: Rule Engine for Claims Triage',
    context: 'Claims triage needs business-configurable rules alongside ML predictions.',
    decision: 'Implement hybrid approach with Drools rule engine and ML scoring.',
    status: 'accepted',
    lastUpdated: '2024-01-03',
    linkedTo: [{ type: 'solution', id: 'sdd-lob4-1' }],
  },
];

type SelectedItem = {
  type: ArchitectureDocType;
  id: string;
} | null;

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  approved: 'bg-emerald-100 text-emerald-700',
  active: 'bg-emerald-100 text-emerald-700',
  deprecated: 'bg-amber-100 text-amber-700',
  proposed: 'bg-blue-100 text-blue-700',
  implemented: 'bg-purple-100 text-purple-700',
  superseded: 'bg-slate-100 text-slate-700',
  accepted: 'bg-emerald-100 text-emerald-700',
};

const lobColors = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', active: 'bg-blue-600 text-white' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', active: 'bg-emerald-600 text-white' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', active: 'bg-purple-600 text-white' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', active: 'bg-amber-600 text-white' },
};

// Transform API metadata to component types
const transformToVision = (meta: ArchitectureDocMetadata): ArchitectureVision => ({
  id: meta.id,
  title: meta.title,
  description: meta.description || '',
  principles: [],
  lastUpdated: meta.lastUpdated,
  status: meta.status as ArchitectureVision['status'],
  systemArchitectures: meta.systemArchitectures || [],
});

const transformToSystem = (meta: ArchitectureDocMetadata): SystemArchitecture => ({
  id: meta.id,
  title: meta.title,
  description: meta.description || '',
  domain: meta.domain || 'General',
  techStack: meta.techStack || [],
  lastUpdated: meta.lastUpdated,
  status: meta.status as SystemArchitecture['status'],
  visionId: meta.parentId || '',
  solutionDesigns: meta.solutionDesigns || [],
});

const transformToSolution = (meta: ArchitectureDocMetadata): SolutionDesignDocument => ({
  id: meta.id,
  title: meta.title,
  description: meta.description || '',
  initiative: meta.initiative || '',
  lastUpdated: meta.lastUpdated,
  status: meta.status as SolutionDesignDocument['status'],
  systemArchitectures: meta.systemArchitectures || [],
  adrs: meta.adrs || [],
});

const transformToADR = (meta: ArchitectureDocMetadata): ArchitectureDecisionRecord => ({
  id: meta.id,
  title: meta.title,
  context: meta.description || '',
  decision: '',
  status: meta.status as ArchitectureDecisionRecord['status'],
  lastUpdated: meta.lastUpdated,
  linkedTo: meta.systemArchitectures?.map(id => ({ type: 'solution' as const, id })) || [],
});

// Helper to get LOB from vision ID
const getLobFromVisionId = (visionId: string): string => {
  const match = visionId.match(/vision-(lob\d)/);
  return match ? match[1] : 'lob1';
};

const ArchitectureBrowser: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, userTier } = useAuthContext();

  // Data state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingSampleData, setUsingSampleData] = useState(false);

  // API data
  const [visions, setVisions] = useState<ArchitectureVision[]>([]);
  const [systems, setSystems] = useState<SystemArchitecture[]>([]);
  const [solutions, setSolutions] = useState<SolutionDesignDocument[]>([]);
  const [adrs, setAdrs] = useState<ArchitectureDecisionRecord[]>([]);

  // UI state
  const [selectedLob, setSelectedLob] = useState<string>('lob1');
  const [selectedItem, setSelectedItem] = useState<SelectedItem>(null);
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Load data from API or fall back to sample data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const hierarchy = await addApi.listArchitectureDocs();

      const hasData = hierarchy.visions.length > 0 ||
        hierarchy.systems.length > 0 ||
        hierarchy.solutions.length > 0 ||
        hierarchy.adrs.length > 0;

      if (hasData) {
        setVisions(hierarchy.visions.map(transformToVision));
        setSystems(hierarchy.systems.map(transformToSystem));
        setSolutions(hierarchy.solutions.map(transformToSolution));
        setAdrs(hierarchy.adrs.map(transformToADR));
        setUsingSampleData(false);

        const systemIds = hierarchy.systems.slice(0, 2).map(s => s.id);
        setExpandedSystems(new Set(systemIds));
      } else {
        loadSampleData();
      }
    } catch (err) {
      console.error('[ArchitectureBrowser] Failed to load from API:', err);
      captureError(err as Error, { component: 'ArchitectureBrowser', action: 'loadData' });
      loadSampleData();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadSampleData = () => {
    setVisions(sampleVisions);
    setSystems(sampleSystems);
    setSolutions(sampleSolutions);
    setAdrs(sampleADRs);
    setUsingSampleData(true);
    setExpandedSystems(new Set(['sys-lob1-1', 'sys-lob1-2']));
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle URL query parameters
  useEffect(() => {
    if (isLoading) return;

    const docType = searchParams.get('doc') as ArchitectureDocType | null;
    const docId = searchParams.get('id');

    if (docType && docId) {
      let exists = false;
      switch (docType) {
        case 'vision':
          exists = visions.some(v => v.id === docId);
          if (exists) {
            const lob = getLobFromVisionId(docId);
            setSelectedLob(lob);
          }
          break;
        case 'system':
          exists = systems.some(s => s.id === docId);
          break;
        case 'solution':
          exists = solutions.some(s => s.id === docId);
          break;
        case 'adr':
          exists = adrs.some(a => a.id === docId);
          break;
      }

      if (exists) {
        setSelectedItem({ type: docType, id: docId });
      }
    } else if (!selectedItem) {
      // Default to first vision of selected LOB
      const lobVision = visions.find(v => v.id.includes(selectedLob));
      if (lobVision) {
        setSelectedItem({ type: 'vision', id: lobVision.id });
      }
    }
  }, [isLoading, searchParams, visions, systems, solutions, adrs, selectedItem, selectedLob]);

  // Filter data by selected LOB
  const filteredVisions = useMemo(() => {
    return visions.filter(v => v.id.includes(selectedLob));
  }, [visions, selectedLob]);

  const filteredSystems = useMemo(() => {
    const lobVisionIds = filteredVisions.map(v => v.id);
    return systems.filter(s => {
      const matchesLob = lobVisionIds.includes(s.visionId);
      const matchesSearch = !searchQuery ||
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesLob && matchesSearch;
    });
  }, [systems, filteredVisions, searchQuery]);

  const toggleSystem = (systemId: string) => {
    setExpandedSystems(prev => {
      const next = new Set(prev);
      if (next.has(systemId)) {
        next.delete(systemId);
      } else {
        next.add(systemId);
      }
      return next;
    });
  };

  const handleLobChange = (lobId: string) => {
    setSelectedLob(lobId);
    // Select the vision for this LOB
    const lobVision = visions.find(v => v.id.includes(lobId));
    if (lobVision) {
      setSelectedItem({ type: 'vision', id: lobVision.id });
    }
    // Expand first two systems of this LOB
    const lobSystems = systems.filter(s => s.visionId.includes(lobId));
    setExpandedSystems(new Set(lobSystems.slice(0, 2).map(s => s.id)));
  };

  const getItemDetails = () => {
    if (!selectedItem) return null;

    switch (selectedItem.type) {
      case 'vision':
        return visions.find(v => v.id === selectedItem.id);
      case 'system':
        return systems.find(s => s.id === selectedItem.id);
      case 'solution':
        return solutions.find(s => s.id === selectedItem.id);
      case 'adr':
        return adrs.find(a => a.id === selectedItem.id);
      default:
        return null;
    }
  };

  const getLinkedSystems = (sddId: string) => {
    const sdd = solutions.find(s => s.id === sddId);
    if (!sdd) return [];
    return systems.filter(sys => sdd.systemArchitectures.includes(sys.id));
  };

  const currentLob = linesOfBusiness.find(l => l.id === selectedLob) || linesOfBusiness[0];

  const renderDetailPanel = () => {
    const item = getItemDetails();
    if (!item) return null;

    if (selectedItem?.type === 'vision') {
      const vision = item as ArchitectureVision;
      return (
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 ${lobColors[currentLob.color].bg} rounded-lg`}>
                <Target size={24} className={lobColors[currentLob.color].text} />
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[vision.status]}`}>
                {vision.status}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{vision.title}</h2>
            <p className="text-slate-600">{vision.description}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Architectural Principles
            </h3>
            <ul className="space-y-2">
              {vision.principles.map((principle, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <ChevronRight size={16} className={lobColors[currentLob.color].text} />
                  {principle}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              System Architectures ({filteredSystems.length})
            </h3>
            <div className="space-y-2">
              {filteredSystems.map(sys => (
                <button
                  key={sys.id}
                  onClick={() => setSelectedItem({ type: 'system', id: sys.id })}
                  className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                >
                  <LayoutDashboard size={18} className="text-emerald-600" />
                  <span className="font-medium text-slate-900">{sys.title}</span>
                  <ChevronRight size={16} className="text-slate-400 ml-auto" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-500 pt-4 border-t border-slate-200">
            <span className="flex items-center gap-1">
              <Clock size={14} />
              Updated {vision.lastUpdated}
            </span>
          </div>
        </div>
      );
    }

    if (selectedItem?.type === 'system') {
      const system = item as SystemArchitecture;
      const linkedSolutions = solutions.filter(s => s.systemArchitectures.includes(system.id));

      return (
        <div className="space-y-6">
          <div>
            <button
              onClick={() => {
                const lobVision = filteredVisions[0];
                if (lobVision) setSelectedItem({ type: 'vision', id: lobVision.id });
              }}
              className={`flex items-center gap-1 text-sm ${lobColors[currentLob.color].text} hover:opacity-80 mb-3`}
            >
              <ArrowLeft size={14} />
              Back to {currentLob.name} Vision
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <LayoutDashboard size={24} className="text-emerald-600" />
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[system.status]}`}>
                {system.status}
              </span>
              <span className="px-2 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-600">
                {system.domain}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{system.title}</h2>
            <p className="text-slate-600">{system.description}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Technology Stack
            </h3>
            <div className="flex flex-wrap gap-2">
              {system.techStack.map(tech => (
                <span key={tech} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Solution Design Documents ({linkedSolutions.length})
            </h3>
            <div className="space-y-2">
              {linkedSolutions.map(sdd => (
                <button
                  key={sdd.id}
                  onClick={() => setSelectedItem({ type: 'solution', id: sdd.id })}
                  className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                >
                  <GitBranch size={18} className="text-purple-600" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-slate-900 block">{sdd.title}</span>
                    <span className="text-sm text-slate-500">{sdd.initiative}</span>
                  </div>
                  <ChevronRight size={16} className="text-slate-400" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-500 pt-4 border-t border-slate-200">
            <span className="flex items-center gap-1">
              <Clock size={14} />
              Updated {system.lastUpdated}
            </span>
          </div>
        </div>
      );
    }

    if (selectedItem?.type === 'solution') {
      const solution = item as SolutionDesignDocument;
      const linkedSystems = getLinkedSystems(solution.id);
      const linkedADRs = adrs.filter(a => solution.adrs.includes(a.id));

      return (
        <div className="space-y-6">
          <div>
            <button
              onClick={() => {
                const firstSystem = linkedSystems[0];
                if (firstSystem) {
                  setSelectedItem({ type: 'system', id: firstSystem.id });
                }
              }}
              className={`flex items-center gap-1 text-sm ${lobColors[currentLob.color].text} hover:opacity-80 mb-3`}
            >
              <ArrowLeft size={14} />
              Back to System Architecture
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <GitBranch size={24} className="text-purple-600" />
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[solution.status]}`}>
                {solution.status}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{solution.title}</h2>
            <p className="text-slate-600">{solution.description}</p>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Tag size={14} />
              <span className="font-medium">{solution.initiative}</span>
            </div>
            {solution.targetDate && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mt-2">
                <Clock size={14} />
                <span>Target: {solution.targetDate}</span>
              </div>
            )}
          </div>

          {linkedSystems.length > 1 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                <span className="flex items-center gap-2">
                  <Layers size={14} />
                  Spans Multiple Systems ({linkedSystems.length})
                </span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {linkedSystems.map(sys => (
                  <button
                    key={sys.id}
                    onClick={() => setSelectedItem({ type: 'system', id: sys.id })}
                    className="flex items-center gap-2 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition-colors text-sm"
                  >
                    <LayoutDashboard size={14} />
                    {sys.title}
                    <ExternalLink size={12} />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Architecture Decision Records ({linkedADRs.length})
            </h3>
            <div className="space-y-2">
              {linkedADRs.map(adr => (
                <button
                  key={adr.id}
                  onClick={() => setSelectedItem({ type: 'adr', id: adr.id })}
                  className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                >
                  <FileText size={18} className="text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-slate-900 block">{adr.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${statusColors[adr.status]}`}>
                      {adr.status}
                    </span>
                  </div>
                  <ChevronRight size={16} className="text-slate-400" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-500 pt-4 border-t border-slate-200">
            <span className="flex items-center gap-1">
              <Clock size={14} />
              Updated {solution.lastUpdated}
            </span>
          </div>
        </div>
      );
    }

    if (selectedItem?.type === 'adr') {
      const adr = item as ArchitectureDecisionRecord;
      const parentSolution = solutions.find(s => s.adrs.includes(adr.id));

      return (
        <div className="space-y-6">
          <div>
            {parentSolution && (
              <button
                onClick={() => setSelectedItem({ type: 'solution', id: parentSolution.id })}
                className={`flex items-center gap-1 text-sm ${lobColors[currentLob.color].text} hover:opacity-80 mb-3`}
              >
                <ArrowLeft size={14} />
                Back to {parentSolution.title}
              </button>
            )}
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText size={24} className="text-amber-600" />
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[adr.status]}`}>
                {adr.status}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{adr.title}</h2>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Context</h3>
            <p className="text-slate-600 bg-slate-50 p-4 rounded-lg">{adr.context}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Decision</h3>
            <p className="text-slate-900 font-medium bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              {adr.decision}
            </p>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-500 pt-4 border-t border-slate-200">
            <span className="flex items-center gap-1">
              <Clock size={14} />
              Updated {adr.lastUpdated}
            </span>
          </div>
        </div>
      );
    }

    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading architecture documentation...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="pt-20 pb-8 sm:pt-24">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 leading-tight text-slate-900">
              Architecture
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Browse and manage your architecture documentation library.
            </p>
          </div>
        </div>
      </section>


      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200">
          <div className="container mx-auto px-6 py-3 max-w-6xl">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle size={16} />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      

      {/* Main Content */}
      <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 min-h-[600px]">
        <div className="container mx-auto px-6 py-8 max-w-6xl">
          {/* LOB Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {linesOfBusiness.map(lob => (
              <button
                key={lob.id}
                onClick={() => handleLobChange(lob.id)}
                className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                  selectedLob === lob.id
                    ? lobColors[lob.color].active + ' shadow-lg'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {lob.name}
              </button>
            ))}
          </div>

          {/* Pro User Quota Banner */}
          {userTier === 'pro' && (
            <div className="mb-6 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl">
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <Crown className="w-4 h-4" />
                  <span>Pro Plan: Manage up to 50 architecture documents</span>
                </div>
                <p className="text-xs text-slate-500">
                  Sign up to Enterprise for unlimited recording and enterprise features
                </p>
              </div>
            </div>
          )}

          {/* LOB Header Card */}
          <div className={`mb-6 p-4 bg-white rounded-xl border-2 ${lobColors[currentLob.color].border} shadow-sm`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${lobColors[currentLob.color].bg} rounded-lg`}>
                  <Building2 size={20} className={lobColors[currentLob.color].text} />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">{currentLob.name}</h2>
                  <p className="text-sm text-slate-500">{currentLob.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {isAuthenticated ? (
                  <Link
                    to="/add"
                    className={`px-3 sm:px-4 py-2 ${lobColors[currentLob.color].active} rounded-lg text-xs sm:text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity`}
                  >
                    <Mic size={16} />
                    <span className="hidden xs:inline">Record</span>
                    <span className="xs:hidden">Record</span>
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="px-3 sm:px-4 py-2 bg-slate-200 text-slate-500 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-2"
                  >
                    <Lock size={14} />
                    <Mic size={16} />
                    <span className="hidden xs:inline">Record Session</span>
                  </Link>
                )}
                {isAuthenticated ? (
                  <Link
                    to="/architecture/import"
                    className="px-3 sm:px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-2 transition-colors"
                  >
                    <FileUp size={16} />
                    <span className="hidden sm:inline">Add Existing Doc</span>
                    <span className="sm:hidden">Add Doc</span>
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="px-3 sm:px-4 py-2 bg-slate-200 text-slate-500 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-2"
                  >
                    <Lock size={14} />
                    <FileUp size={16} />
                    <span className="hidden sm:inline">Add Doc</span>
                  </Link>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Panel - Navigation Tree */}
            <div className="w-full lg:w-80 lg:flex-shrink-0">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Search */}
                <div className="p-4 border-b border-slate-200">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search systems..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Tree View */}
                <div className="p-3">
                  {/* Vision */}
                  {filteredVisions.map(vision => (
                    <button
                      key={vision.id}
                      onClick={() => setSelectedItem({ type: 'vision', id: vision.id })}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors text-left ${
                        selectedItem?.type === 'vision' && selectedItem.id === vision.id
                          ? `${lobColors[currentLob.color].bg} ${lobColors[currentLob.color].text}`
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      <Target size={18} className={lobColors[currentLob.color].text} />
                      <span className="font-medium text-sm truncate">{vision.title || 'Architecture Vision'}</span>
                    </button>
                  ))}

                  {/* Systems */}
                  <div className="ml-2 mt-1 border-l-2 border-slate-200 pl-2">
                    {filteredSystems.map(system => {
                      const isExpanded = expandedSystems.has(system.id);
                      const linkedSolutions = solutions.filter(s =>
                        s.systemArchitectures.includes(system.id)
                      );

                      return (
                        <div key={system.id}>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleSystem(system.id)}
                              className="p-1 hover:bg-slate-100 rounded"
                            >
                              {isExpanded ? (
                                <ChevronDown size={14} className="text-slate-400" />
                              ) : (
                                <ChevronRight size={14} className="text-slate-400" />
                              )}
                            </button>
                            <button
                              onClick={() => setSelectedItem({ type: 'system', id: system.id })}
                              className={`flex-1 flex items-center gap-2 p-2 rounded-lg transition-colors text-left ${
                                selectedItem?.type === 'system' && selectedItem.id === system.id
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'hover:bg-slate-50 text-slate-700'
                              }`}
                            >
                              <LayoutDashboard size={16} className="text-emerald-500" />
                              <span className="text-sm truncate">{system.title}</span>
                            </button>
                          </div>

                          {/* Solutions */}
                          {isExpanded && (
                            <div className="ml-5 mt-1 border-l-2 border-slate-200 pl-2">
                              {linkedSolutions.map(sdd => (
                                <button
                                  key={sdd.id}
                                  onClick={() => setSelectedItem({ type: 'solution', id: sdd.id })}
                                  className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors text-left ${
                                    selectedItem?.type === 'solution' && selectedItem.id === sdd.id
                                      ? 'bg-purple-50 text-purple-700'
                                      : 'hover:bg-slate-50 text-slate-700'
                                  }`}
                                >
                                  <GitBranch size={14} className="text-purple-500 flex-shrink-0" />
                                  <span className="text-sm truncate flex-1">{sdd.title}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Detail View */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 min-h-[400px] lg:min-h-[500px]">
                {renderDetailPanel()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ArchitectureBrowser;
